import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  lisReviewApi,
  type LisReviewConfig,
  type LisReviewListParams,
} from '@/api/lisReviewApi'
import { notificationKeys } from '@/hooks/useNotifications'
import { useTenant } from '@/providers/TenantProvider'

// Every key carries the selected tenant, so a switch cannot paint another
// tenant's queue. The shared prefix lets one invalidation refresh the badge,
// the list and the open detail together.
export const lisReviewKey = (tenantId: string | null) => ['lis-review', tenantId] as const

/** Pending counts per procedure / submission / MRN — drives every orange badge. */
export function useLisReviewSummary(enabled = true) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...lisReviewKey(selectedTenantId), 'summary'],
    queryFn: () => lisReviewApi.summary(),
    enabled: !!selectedTenantId && enabled,
  })
}

export function useLisReviewList(params: LisReviewListParams, enabled = true) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...lisReviewKey(selectedTenantId), 'list', params],
    queryFn: () => lisReviewApi.list(params),
    enabled: !!selectedTenantId && enabled,
  })
}

export function useLisReviewDetail(id: string | null) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...lisReviewKey(selectedTenantId), 'detail', id],
    queryFn: () => lisReviewApi.detail(id as string),
    enabled: !!selectedTenantId && !!id,
  })
}

function useRefreshAfterDecision() {
  const qc = useQueryClient()
  const { selectedTenantId } = useTenant()
  return () => {
    void qc.invalidateQueries({ queryKey: lisReviewKey(selectedTenantId) })
    // A decision clears the pending state, which the bell and nav badges count.
    void qc.invalidateQueries({ queryKey: notificationKeys })
  }
}

export function useApproveLisResult() {
  const refresh = useRefreshAfterDecision()
  return useMutation({
    mutationFn: (id: string) => lisReviewApi.approve(id),
    onSuccess: refresh,
  })
}

export function useRejectLisResult() {
  const refresh = useRefreshAfterDecision()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => lisReviewApi.reject(id, reason),
    onSuccess: refresh,
  })
}

export function useLisReviewConfig(enabled = true) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...lisReviewKey(selectedTenantId), 'config'],
    queryFn: () => lisReviewApi.getConfig(),
    enabled: !!selectedTenantId && enabled,
  })
}

export function useSaveLisReviewConfig() {
  const qc = useQueryClient()
  const { selectedTenantId } = useTenant()
  return useMutation({
    mutationFn: (config: LisReviewConfig) => lisReviewApi.putConfig(config),
    onSuccess: (saved) => {
      qc.setQueryData([...lisReviewKey(selectedTenantId), 'config'], saved)
    },
  })
}
